import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getBrowserId } from '@/lib/pushDevices';
import { buildSessionKey, startLiveView, endLiveView, updateLivePeak } from '@/lib/zcall';
import { triggerLivePush } from '@/lib/notifyPush';
import { fetchSfuConfig, type SfuConfig } from '@/lib/zcallSfu';
import { useZCallWebRTC } from './zcall/useZCallWebRTC';
import { useZCallSfu } from './zcall/useZCallSfu';
import { useRoomControl, youtubeEmbedUrl } from './zcall/useRoomControl';
import { useZCallRecorder } from './zcall/useZCallRecorder';
import VideoTile from './zcall/components/VideoTile';
import CallControls from './zcall/components/CallControls';
import NotepadPanel from './zcall/components/NotepadPanel';
import SharedFilesPanel from './zcall/components/SharedFilesPanel';
import PeoplePanel from './zcall/components/PeoplePanel';
import VotePanel from './zcall/components/VotePanel';
import YoutubePanel from './zcall/components/YoutubePanel';
import HostControls from './zcall/components/HostControls';
import SignupModal from './zcall/components/SignupModal';
import CameraTestPanel from './zcall/components/CameraTestPanel';

// ─── Logo de marque ZIFEK ───────────────────────────────────────────────
const BRAND_LOGO = 'https://storage.helloreaddy.io/project_files/59392c9e-e303-496e-bac1-59ba5941cf65/07452721-5131-4b23-922e-acc73d68a0a9_compressed_zifek.webp';

interface MeetingInfo {
  id: number;
  user_id: number;
  title: string;
  room_id: string;
  slug: string | null;
  scheduled_at: string | null;
  duration_minutes: number;
  status: string;
  kind: string | null;
  created_at: string;
}

type Panel = 'none' | 'notes' | 'files' | 'people';

/** Fin attendue d'une réunion : début (planifié ou création) + durée. */
function meetingEndMs(m: MeetingInfo): number {
  const start = m.scheduled_at ? new Date(m.scheduled_at).getTime() : new Date(m.created_at).getTime();
  const dur = (m.duration_minutes || 90) * 60_000;
  return start + dur;
}

/** Marge avant fermeture automatique (minutes) au-delà de la durée prévue. */
const AUTO_END_GRACE_MS = 10 * 60_000;

export default function ZCallRoomPage() {
  const { slug, roomId } = useParams<{ slug?: string; roomId?: string }>();
  const navigate = useNavigate();
  const identifier = slug || roomId;
  const { user } = useAuth();

  const [meeting, setMeeting] = useState<MeetingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joined, setJoined] = useState(false);
  const [joinedName, setJoinedName] = useState('Participant');
  const [displayName, setDisplayName] = useState('');
  const [panel, setPanel] = useState<Panel>('none');
  const [showSignup, setShowSignup] = useState(false);
  const [cameraTestOpen, setCameraTestOpen] = useState(false);
  const [recordMsg, setRecordMsg] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Sondage (hôte)
  const [voteOpen, setVoteOpen] = useState(false);
  const [voteQuestion, setVoteQuestion] = useState('');
  const [voteOptions, setVoteOptions] = useState<string[]>(['', '']);
  // YouTube (hôte)
  const [ytOpen, setYtOpen] = useState(false);
  const [ytInput, setYtInput] = useState('');

  const isHost = !!(user && meeting && Number(user.id) === Number(meeting.user_id));
  const isLive = meeting?.kind === 'live';
  // En direct (live) : seul l'hôte diffuse ; les autres sont spectateurs.
  const publish = !isLive || isHost;

  // Nom pré-rempli depuis la session.
  useEffect(() => {
    if (user?.name) setDisplayName(user.name);
    else if (user?.user_name) setDisplayName(user.user_name);
  }, [user]);

  // ─── Chargement de la réunion ──────────────────────────────────────────
  useEffect(() => {
    if (!identifier) {
      setError('Aucun identifiant de réunion fourni.');
      setLoading(false);
      return;
    }
    const fetchMeeting = async () => {
      setLoading(true);
      setError(null);
      const MEETING_COLS =
        'id, user_id, title, room_id, slug, scheduled_at, duration_minutes, status, kind, created_at';
      const key = decodeURIComponent(identifier).trim();

      // On cherche d'abord par slug (lien lisible), puis par room_id.
      // Deux requêtes distinctes plutôt qu'un filtre `or` : plus robuste aux
      // caractères spéciaux d'un identifiant d'invitation.
      let data: MeetingInfo | null = null;
      let fetchErr: unknown = null;
      try {
        const bySlug = await supabase.from('zcall_meetings').select(MEETING_COLS).eq('slug', key).maybeSingle();
        if (bySlug.data) {
          data = bySlug.data as MeetingInfo;
        } else {
          const byRoom = await supabase.from('zcall_meetings').select(MEETING_COLS).eq('room_id', key).maybeSingle();
          data = (byRoom.data as MeetingInfo) || null;
          fetchErr = byRoom.error || bySlug.error;
        }
      } catch (e) {
        fetchErr = e;
      }

      if (fetchErr || !data) {
        setError(
          fetchErr
            ? "Impossible de charger la réunion pour le moment. Vérifiez votre connexion puis réessayez."
            : "Cette réunion n'existe pas ou le lien est invalide.",
        );
      } else if (data.status === 'cancelled') {
        setError('Cette réunion a été annulée.');
      } else if (data.status === 'ended') {
        setError('Cette réunion est terminée.');
      } else if (
        (data.status === 'active' || data.status === 'scheduled') &&
        Date.now() > meetingEndMs(data as MeetingInfo) + AUTO_END_GRACE_MS
      ) {
        // Fermeture AUTOMATIQUE : la réunion a dépassé sa durée prévue.
        await supabase.from('zcall_meetings').update({ status: 'ended' }).eq('id', data.id);
        setError('Cette réunion est terminée.');
      } else {
        setMeeting(data as MeetingInfo);
      }
      setLoading(false);
    };
    fetchMeeting();
  }, [identifier, reloadKey]);

  const sessionKey = useMemo(() => buildSessionKey(user?.id ?? null, getBrowserId()), [user?.id]);

  // ─── Transport média (SFU si activé, sinon maillage direct) ──────────────
  const [sfuConfig, setSfuConfig] = useState<SfuConfig>({ enabled: false, provider: 'livekit', url: '' });
  const [transport, setTransport] = useState<'mesh' | 'sfu'>('mesh');
  const fellBackRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    fetchSfuConfig().then((cfg) => { if (mounted) setSfuConfig(cfg); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    setTransport(sfuConfig.enabled && sfuConfig.url ? 'sfu' : 'mesh');
  }, [sfuConfig]);

  const callActive = joined && !!meeting && !!meeting.room_id;
  const mesh = useZCallWebRTC(meeting?.room_id || '', joinedName, callActive && transport === 'mesh', publish);
  const sfu = useZCallSfu(meeting?.room_id || '', joinedName, callActive && transport === 'sfu', sfuConfig.url, publish);

  useEffect(() => {
    if (transport === 'sfu' && sfu.status === 'error' && !fellBackRef.current) {
      fellBackRef.current = true;
      setTransport('mesh');
    }
  }, [transport, sfu.status]);

  const call = transport === 'sfu' ? sfu : mesh;

  // ─── Couche temps réel (participants, modération, vote, YouTube) ────────
  const rc = useRoomControl({
    roomId: meeting?.room_id || '',
    selfName: joinedName,
    active: callActive,
    isHost,
    publishing: publish,
    presenting: call.screenSharing,
  });

  const recorder = useZCallRecorder();

  // L'hôte a coupé tous les micros → on force le micro des autres à l'arrêt.
  useEffect(() => {
    if (!callActive) return;
    if (rc.mutedAll && !rc.isGranted && call.micOn) call.setMicEnabled(false);
  }, [callActive, rc.mutedAll, rc.isGranted, call.micOn, call]);

  // ─── Notification push automatique quand un LIVE démarre ────────────────
  const livePushSentRef = useRef(false);
  useEffect(() => {
    if (isHost && isLive && joined && meeting && !livePushSentRef.current) {
      livePushSentRef.current = true;
      void triggerLivePush(meeting.title, meeting.room_id);
    }
  }, [isHost, isLive, joined, meeting]);

  const participantsCount = rc.participants.length || call.remotes.length + 1;

  // ─── Statistiques de spectateurs du live ────────────────────────────────
  // Un spectateur (hors animateur) ouvre une session de vue, clôturée au
  // départ avec son temps de visionnage ; l'animateur enregistre le pic.
  const liveViewIdRef = useRef<number | null>(null);
  const liveWatchStartRef = useRef<number>(0);
  const liveMeetingId = meeting?.id ?? null;
  const liveRoomId = meeting?.room_id || '';
  useEffect(() => {
    if (!joined || !isLive || isHost || !liveMeetingId || !liveRoomId) return undefined;
    liveWatchStartRef.current = Date.now();
    void startLiveView(liveMeetingId, liveRoomId, user?.id ?? null, getBrowserId())
      .then((id) => { liveViewIdRef.current = id; });
    return () => {
      const watched = Math.round((Date.now() - liveWatchStartRef.current) / 1000);
      void endLiveView(liveViewIdRef.current, watched);
      liveViewIdRef.current = null;
    };
  }, [joined, isLive, isHost, liveMeetingId, liveRoomId, user?.id]);

  useEffect(() => {
    if (!joined || !isLive || !isHost || !liveMeetingId) return;
    void updateLivePeak(liveMeetingId, Math.max(0, participantsCount - 1));
  }, [joined, isLive, isHost, liveMeetingId, participantsCount]);

  const activePresenter = rc.participants.find((p) => p.presenting);

  /** Retrouve les métadonnées (hôte / présente) d'un pair média. */
  const peerMeta = (peerId: string) => {
    const p = rc.participants.find((x) => x.id === peerId);
    return { isHost: !!p?.isHost, presenting: !!p?.presenting };
  };

  const acceptPresenter = async () => {
    rc.acknowledgePresenter();
    try {
      await call.startScreenShare();
    } catch {
      /* partage refusé */
    }
  };

  const handleJoin = () => {
    setJoinedName(displayName.trim() || 'Participant');
    setJoined(true);
  };

  const leave = () => {
    void recorder.stop();
    navigate('/');
  };

  // ─── Enregistrement de la réunion ───────────────────────────────────────
  const toggleRecord = async () => {
    if (!recorder.recording) {
      const sources = [
        { stream: call.localStream, name: `${joinedName} (vous)` },
        ...call.remotes.map((r) => ({ stream: r.stream, name: r.name })),
      ];
      recorder.start(sources);
      setRecordMsg(null);
    } else {
      const duration = recorder.elapsed;
      const blob = await recorder.stop();
      if (!blob) return;
      if (user?.id) {
        setRecordMsg('Envoi de l\u2019enregistrement…');
        const url = await recorder.upload(blob, user.id, meeting?.title || 'ZCall', {
          roomId: meeting?.room_id,
          meetingId: meeting?.id ?? null,
          durationSeconds: duration,
        });
        setRecordMsg(url ? 'Enregistrement sauvegardé dans Médias (dossier ZCall).' : "Échec de l'enregistrement.");
      } else {
        setRecordMsg('Connectez-vous pour sauvegarder l\u2019enregistrement.');
      }
      window.setTimeout(() => setRecordMsg(null), 5000);
    }
  };

  const launchVote = () => {
    rc.startVote(voteQuestion, voteOptions);
    setVoteOpen(false);
    setVoteQuestion('');
    setVoteOptions(['', '']);
  };

  const launchYoutube = () => {
    const embed = youtubeEmbedUrl(ytInput);
    if (!embed) return;
    rc.setYoutube({ url: embed, playing: true });
    setYtOpen(false);
    setYtInput('');
  };

  // ─── Chargement ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
            <i className="ri-loader-4-line text-2xl text-foreground-400 animate-spin"></i>
          </div>
          <p className="text-sm text-foreground-500">Chargement...</p>
        </div>
      </div>
    );
  }

  // ─── Erreur ─────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
            <i className="ri-video-off-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="text-lg font-semibold text-foreground-900 mb-2">Réunion indisponible</h1>
          <p className="text-sm text-foreground-500 mb-6">{error}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
            <button
              onClick={() => { setError(null); setReloadKey((k) => k + 1); }}
              className="px-6 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-semibold cursor-pointer hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-refresh-line mr-1.5"></i>
              Réessayer
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium cursor-pointer hover:bg-background-200/70 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-1.5"></i>
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Écran d'accueil de la réunion ──────────────────────────────────────
  if (!joined && meeting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 p-4">
        <CameraTestPanel open={cameraTestOpen} onClose={() => setCameraTestOpen(false)} />
        <div className="max-w-md w-full">
          <div className="bg-background-50 border border-background-200/70 rounded-2xl p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl overflow-hidden border border-background-200/70 bg-background-100">
              <img src={BRAND_LOGO} alt="ZIFEK" title="ZIFEK" className="w-full h-full object-cover" />
            </div>
            <p className="text-sm font-heading font-bold text-accent-600 mb-0.5">ZCall</p>
            <p className="text-xs font-medium text-foreground-500 uppercase tracking-widest mb-3">
              {isLive ? 'Diffusion en direct' : 'Visioconférence'}
            </p>
            <h1 className="text-lg font-semibold text-foreground-900 mb-1">{meeting.title}</h1>
            {meeting.scheduled_at ? (
              <p className="text-xs text-foreground-500 mb-6">
                <i className="ri-calendar-event-line mr-1"></i>
                {new Date(meeting.scheduled_at).toLocaleDateString('fr-FR', {
                  day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
                })}
                {' · '}{meeting.duration_minutes} min
              </p>
            ) : (
              <p className="text-xs text-foreground-500 mb-6">Réunion instantanée · {meeting.duration_minutes} min</p>
            )}

            {isLive && (
              <div className="mb-5 px-3 py-2.5 rounded-xl bg-accent-50 border border-accent-200/60 text-left">
                <p className="text-xs text-accent-700 flex items-center gap-1.5">
                  <i className="ri-live-line"></i>
                  {isHost ? 'Vous êtes l\u2019animateur : vous diffusez le live.' : 'Vous rejoignez en tant que spectateur (sans caméra ni micro).'}
                </p>
              </div>
            )}

            <div className="text-left mb-6">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Votre nom</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Ex: Marie Dupont"
                maxLength={40}
                className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-xl bg-background-50 text-foreground-800 focus:outline-none focus:border-accent-300 transition-colors"
              />
              {user ? (
                <p className="text-[11px] text-accent-600 mt-1.5 flex items-center gap-1">
                  <i className="ri-account-circle-line"></i>
                  Connecté — votre bloc-notes sera enregistré dans vos documents.
                </p>
              ) : (
                <p className="text-[11px] text-foreground-400 mt-1.5 flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  Sans compte, vous restez anonyme.
                </p>
              )}
            </div>

            {!isLive || isHost ? (
              <button
                onClick={() => setCameraTestOpen(true)}
                className="w-full mb-2 py-2.5 bg-background-100 text-foreground-700 rounded-full text-xs font-semibold cursor-pointer hover:bg-background-200/70 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-vidicon-line"></i>
                Tester ma caméra
              </button>
            ) : null}

            <button
              onClick={handleJoin}
              className="w-full py-3 bg-accent-500 text-background-50 rounded-full text-sm font-semibold cursor-pointer hover:bg-accent-600 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <i className={isLive && !isHost ? 'ri-live-line' : 'ri-vidicon-line'}></i>
              {isLive && !isHost ? 'Rejoindre le live' : 'Rejoindre la réunion'}
            </button>
            {!user && (
              <button
                onClick={() => { setJoinedName(displayName.trim() || 'Participant'); setJoined(true); }}
                className="mt-2 w-full py-2.5 bg-background-100 text-foreground-700 rounded-full text-xs font-medium cursor-pointer hover:bg-background-200/70 transition-colors whitespace-nowrap"
              >
                Rejoindre sans compte
              </button>
            )}
            <p className="text-[10px] text-foreground-400 mt-3">
              {isLive && !isHost
                ? 'En tant que spectateur, vous ne diffusez ni caméra ni micro.'
                : 'En rejoignant, vous acceptez que votre caméra et votre microphone soient activés.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Salle de visioconférence ───────────────────────────────────────────
  const ytEmbed = rc.youtube ? youtubeEmbedUrl(rc.youtube.url) || rc.youtube.url : null;

  return (
    <div className="fixed inset-0 z-50 bg-foreground-950 flex flex-col">
      {/* Barre supérieure */}
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={leave}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white cursor-pointer hover:bg-white/20 transition-colors flex-shrink-0"
            title="Quitter"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-6 h-6 rounded-md overflow-hidden flex items-center justify-center shrink-0">
              <img src={BRAND_LOGO} alt="ZIFEK" title="ZIFEK" className="w-full h-full object-cover" />
            </span>
            <span className="text-white text-sm font-semibold font-heading">ZCall</span>
            <span
              className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${transport === 'sfu' ? 'bg-primary-500/30 text-primary-100' : 'bg-white/10 text-white/70'}`}
              title={transport === 'sfu' ? 'Diffusion via le serveur média' : 'Connexion directe (peer-to-peer)'}
            >
              {transport === 'sfu' ? 'SFU' : 'Direct'}
            </span>
            {isLive && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold bg-red-500 text-white flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>LIVE
              </span>
            )}
            <span className="w-1 h-1 rounded-full bg-white/30"></span>
            <span className="text-white/80 text-xs font-medium truncate max-w-[200px]">{meeting?.title}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-white/60 text-[11px] flex items-center gap-1">
            <i className="ri-group-line"></i>{participantsCount}
          </span>
          <span className="hidden sm:inline text-white/50 text-[11px] tabular-nums flex items-center gap-1">
            <i className="ri-pulse-line"></i>{rc.latencyMs != null ? `${rc.latencyMs} ms` : '—'}
          </span>
          <span className="hidden sm:inline text-white/50 text-[11px] tabular-nums">
            <i className="ri-time-line mr-1"></i>{meeting?.duration_minutes} min
          </span>
          {call.status === 'connecting' && (
            <span className="text-amber-400 text-[11px] flex items-center gap-1">
              <i className="ri-loader-4-line animate-spin"></i>Connexion…
            </span>
          )}
          {recorder.recording && (
            <span className="text-red-400 text-[11px] flex items-center gap-1 font-semibold">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>REC
            </span>
          )}
        </div>
      </div>

      {/* Barre d'outils de l'hôte */}
      {isHost && (
        <HostControls
          mutedAll={rc.mutedAll}
          onToggleMuteAll={() => rc.setMutedAll(!rc.mutedAll)}
          recording={recorder.recording}
          elapsed={recorder.elapsed}
          onToggleRecord={toggleRecord}
          handsRaised={rc.handsRaised}
          onGrant={rc.grant}
          onGrantAll={rc.grantEveryone}
          onOpenVote={() => setVoteOpen(true)}
          onOpenYoutube={() => setYtOpen(true)}
          youtubeActive={!!rc.youtube}
          onStopYoutube={() => rc.setYoutube(null)}
          presenterName={rc.presenterName}
          onStopPresenter={() => rc.designatePresenter(null)}
        />
      )}

      {/* Corps */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        <div className="flex-1 min-w-0 min-h-0 overflow-y-auto p-2 md:p-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 md:gap-2.5 max-w-[1100px] mx-auto">
            <VideoTile
              stream={call.localStream}
              name={joinedName}
              muted
              isLocal
              isHost={isHost}
              micOff={!call.micOn}
              videoOff={!call.camOn && !call.screenSharing}
              screen={call.screenSharing}
            />
            {call.remotes.map((p) => (
              <VideoTile
                key={p.id}
                stream={p.stream}
                name={p.name}
                videoOff={!p.stream}
                isHost={peerMeta(p.id).isHost}
                presenting={peerMeta(p.id).presenting}
              />
            ))}
          </div>
          {!publish && (
            <div className="mt-4 px-4 py-2.5 rounded-lg bg-accent-500/10 border border-accent-500/30 text-accent-200 text-xs flex items-center gap-2">
              <i className="ri-live-line"></i>Mode spectateur — vous regardez le live sans diffuser.
            </div>
          )}
          {activePresenter && (
            <div className="mt-4 px-4 py-2.5 rounded-lg bg-accent-500/15 border border-accent-500/30 text-accent-100 text-xs flex items-center gap-2">
              <i className="ri-presentation-line"></i>
              <span><strong>{activePresenter.name}</strong> présente son écran.</span>
            </div>
          )}
          {call.error && (
            <div className="mt-4 px-4 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs flex items-center gap-2">
              <i className="ri-error-warning-line"></i>{call.error}
            </div>
          )}
          {recordMsg && (
            <div className="mt-4 px-4 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white text-xs flex items-center gap-2">
              <i className="ri-movie-line"></i>{recordMsg}
            </div>
          )}
        </div>

        {panel !== 'none' && (
          <aside className="w-full lg:w-80 flex-shrink-0 bg-foreground-950 border-t lg:border-t-0 lg:border-l border-white/10 max-h-[45vh] lg:max-h-none flex flex-col">
            {panel === 'notes' && (
              <NotepadPanel
                roomId={meeting?.room_id || ''}
                sessionKey={sessionKey}
                userId={user?.id ?? null}
                userName={joinedName}
                meetingTitle={meeting?.title || 'Réunion ZCall'}
                onRequireAccount={() => setShowSignup(true)}
              />
            )}
            {panel === 'files' && (
              <SharedFilesPanel roomId={meeting?.room_id || ''} userId={user?.id ?? null} userName={joinedName} />
            )}
            {panel === 'people' && (
              <PeoplePanel
                participants={rc.participants}
                selfId={rc.selfId}
                latencyMs={rc.latencyMs}
                isHost={isHost}
                presenterName={rc.presenterName}
                onDesignate={(p) => rc.designatePresenter(p)}
              />
            )}
          </aside>
        )}
      </div>

      {/* Overlays flottants : sondage + vidéo YouTube */}
      {(rc.vote || ytEmbed) && (
        <div className="pointer-events-none fixed bottom-24 right-4 z-[60] flex flex-col gap-3 items-end">
          {rc.vote && (
            <div className="pointer-events-auto">
              <VotePanel
                vote={rc.vote}
                counts={rc.voteCounts}
                myVote={rc.myVote}
                isHost={isHost}
                onVote={rc.castVote}
                onClose={rc.closeVote}
              />
            </div>
          )}
          {ytEmbed && (
            <div className="pointer-events-auto">
              <YoutubePanel embedUrl={ytEmbed} isHost={isHost} onClose={() => rc.setYoutube(null)} />
            </div>
          )}
        </div>
      )}

      {/* Contrôles */}
      <div className="border-t border-white/10 flex-shrink-0">
        <CallControls
          micOn={call.micOn}
          camOn={call.camOn}
          screenSharing={call.screenSharing}
          panel={panel}
          onToggleMic={call.toggleMic}
          onToggleCam={call.toggleCam}
          onToggleScreen={() => { void (call.screenSharing ? call.stopScreenShare() : call.startScreenShare()); }}
          onTogglePanel={setPanel}
          onToggleTest={() => setCameraTestOpen(true)}
          onLeave={leave}
          showHand={rc.mutedAll && !isHost}
          handRaised={rc.handsRaised.some((h) => h.id === rc.selfId)}
          onToggleHand={() => rc.setOwnHand(!rc.handsRaised.some((h) => h.id === rc.selfId))}
        />
      </div>

      {/* Test caméra/micro accessible pendant la réunion */}
      <CameraTestPanel open={cameraTestOpen} onClose={() => setCameraTestOpen(false)} />

      {/* Modal sondage (hôte) */}
      {voteOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setVoteOpen(false)}></div>
          <div className="relative w-full max-w-md bg-background-50 rounded-2xl p-6">
            <h3 className="text-lg font-semibold font-heading text-foreground-950 mb-4">Lancer un sondage</h3>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Question</label>
            <input
              value={voteQuestion}
              onChange={(e) => setVoteQuestion(e.target.value)}
              placeholder="Ex: Quel thème préférez-vous ?"
              className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-xl bg-background-50 text-foreground-800 focus:outline-none focus:border-accent-300 mb-4"
            />
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Réponses</label>
            <div className="space-y-2 mb-3">
              {voteOptions.map((opt, i) => (
                <input
                  key={i}
                  value={opt}
                  onChange={(e) => setVoteOptions((prev) => prev.map((o, j) => (j === i ? e.target.value : o)))}
                  placeholder={`Réponse ${i + 1}`}
                  className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-xl bg-background-50 text-foreground-800 focus:outline-none focus:border-accent-300"
                />
              ))}
            </div>
            {voteOptions.length < 4 && (
              <button
                onClick={() => setVoteOptions((p) => [...p, ''])}
                className="text-xs text-accent-600 hover:text-accent-700 cursor-pointer mb-4"
              >
                <i className="ri-add-line mr-1"></i>Ajouter une réponse
              </button>
            )}
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => setVoteOpen(false)} className="px-4 py-2 rounded-full text-sm text-foreground-600 hover:bg-background-100 cursor-pointer">Annuler</button>
              <button
                onClick={launchVote}
                disabled={!voteQuestion.trim() || voteOptions.filter((o) => o.trim()).length < 2}
                className="px-5 py-2 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                Lancer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal YouTube (hôte) */}
      {ytOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setYtOpen(false)}></div>
          <div className="relative w-full max-w-md bg-background-50 rounded-2xl p-6">
            <h3 className="text-lg font-semibold font-heading text-foreground-950 mb-4">Partager une vidéo YouTube</h3>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Lien YouTube</label>
            <input
              value={ytInput}
              onChange={(e) => setYtInput(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-xl bg-background-50 text-foreground-800 focus:outline-none focus:border-accent-300 mb-2"
            />
            <p className="text-[11px] text-foreground-400 mb-4">La vidéo sera lue dans la salle par tous les participants.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setYtOpen(false)} className="px-4 py-2 rounded-full text-sm text-foreground-600 hover:bg-background-100 cursor-pointer">Annuler</button>
              <button
                onClick={launchYoutube}
                disabled={!youtubeEmbedUrl(ytInput)}
                className="px-5 py-2 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                Diffuser
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invitation à présenter : l'hôte a désigné ce participant */}
      {rc.presenterRequested && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>
          <div className="relative w-full max-w-sm bg-background-50 rounded-2xl p-6 text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-accent-100 flex items-center justify-center">
              <i className="ri-presentation-line text-2xl text-accent-600"></i>
            </div>
            <h3 className="text-base font-semibold font-heading text-foreground-950 mb-1">Vous êtes désigné présentateur</h3>
            <p className="text-xs text-foreground-500 mb-5">L&apos;hôte vous invite à partager votre écran avec les participants.</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={acceptPresenter}
                className="w-full py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-semibold cursor-pointer hover:bg-accent-600 whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-computer-line"></i>Partager mon écran
              </button>
              <button
                onClick={() => rc.acknowledgePresenter()}
                className="w-full py-2.5 bg-background-100 text-foreground-600 rounded-full text-sm font-medium cursor-pointer hover:bg-background-200/70 whitespace-nowrap"
              >
                Plus tard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Création de compte sans quitter la salle */}
      <SignupModal
        open={showSignup}
        onClose={() => setShowSignup(false)}
        onCreated={() => { setShowSignup(false); setPanel('notes'); }}
      />
    </div>
  );
}